import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { bearing, haversineKm, interpolateGreatCircle } from "../lib/geo.js";
import "./preview.css";

// Full-screen 3D cockpit preview overlay.
//
// Given a route ({ origin, destination } airport objects with latitude/longitude)
// and a departure time, it lazy-loads CesiumJS and flies a cockpit camera along the
// great-circle path, with an astronomically-correct sky for the flight's time and
// predicted clouds/weather sampled per-waypoint from /api/forecast.
//
// Cesium is heavy (~several MB) so it is imported dynamically the first time the
// overlay opens — it is never part of the main app bundle.

const CRUISE_ALT_M = 11000; // ~FL360, a believable airliner cruise
const PATH_STEPS = 160; // samples along the great circle

// Esri World Imagery — the same satellite tiles the 2D map already uses, so the
// preview needs no Cesium ion account. World terrain is opt-in via a token below.
const ESRI_IMAGERY =
  "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";

function hasCoords(p) {
  return p && Number.isFinite(p.latitude) && Number.isFinite(p.longitude);
}

function fmtAirport(a) {
  return a?.iata || a?.icao || a?.name || "?";
}

// A soft radial-gradient puff, used as the cloud billboard texture. Generated once.
function makeCloudTexture() {
  const c = document.createElement("canvas");
  c.width = c.height = 128;
  const ctx = c.getContext("2d");
  const g = ctx.createRadialGradient(64, 64, 4, 64, 64, 60);
  g.addColorStop(0, "rgba(255,255,255,0.95)");
  g.addColorStop(0.5, "rgba(255,255,255,0.55)");
  g.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(64, 64, 60, 0, Math.PI * 2);
  ctx.fill();
  return c;
}

export default function Preview3D({ open, route, departureTime, terrain = true, onLucky, onClose }) {
  const containerRef = useRef(null);
  const viewerRef = useRef(null);
  const cesiumRef = useRef(null); // the loaded Cesium module
  const cloudTextureRef = useRef(null); // cached cloud sprite (kept off the frozen Cesium module)

  const [phase, setPhase] = useState("idle"); // idle | loading | ready | error
  const [error, setError] = useState("");
  const [fraction, setFraction] = useState(0); // 0..1 position along the route
  const [flying, setFlying] = useState(false);
  const [weather, setWeather] = useState(null); // forecast at the current point
  const flyRaf = useRef(null);

  // When opened without a route (from the topbar), the user types one here.
  const [manualRoute, setManualRoute] = useState(null);
  const [depTime, setDepTime] = useState(null);

  // Reset manual state each time the overlay is closed.
  useEffect(() => {
    if (!open) {
      setManualRoute(null);
      setDepTime(null);
    }
  }, [open]);

  const effRoute = route || manualRoute;
  const effDeparture = departureTime || depTime || undefined;
  const canPreview = hasCoords(effRoute?.origin) && hasCoords(effRoute?.destination);

  // Great-circle path + trip geometry (recomputed only when the route changes).
  const geometry = useMemo(() => {
    if (!canPreview) return null;
    const o = effRoute.origin;
    const d = effRoute.destination;
    const path = interpolateGreatCircle(o.latitude, o.longitude, d.latitude, d.longitude, PATH_STEPS);
    const totalKm = haversineKm(o.latitude, o.longitude, d.latitude, d.longitude);
    // Cruise speed ~830 km/h → total flight duration in ms (for sun/clock stepping).
    const durationMs = (totalKm / 830) * 3600 * 1000;
    return { path, totalKm, durationMs };
  }, [effRoute, canPreview]);

  // ── Load Cesium + build the scene when the overlay opens with a valid route ──
  useEffect(() => {
    if (!open || !canPreview) return;
    let cancelled = false;
    setPhase("loading");
    setError("");

    (async () => {
      try {
        // Tell Cesium where its runtime static assets live (copied to /cesium by
        // vite-plugin-static-copy). Must be set before the engine initialises.
        if (!window.CESIUM_BASE_URL) window.CESIUM_BASE_URL = "/cesium/";
        const Cesium = cesiumRef.current || (await import("cesium"));
        await import("cesium/Build/Cesium/Widgets/widgets.css");
        if (cancelled) return;
        cesiumRef.current = Cesium;

        const token = import.meta.env.VITE_CESIUM_ION_TOKEN;
        if (token) Cesium.Ion.defaultAccessToken = token;

        const viewer = new Cesium.Viewer(containerRef.current, {
          // No account needed: open satellite imagery, no ion imagery/terrain picker.
          baseLayerPicker: false,
          geocoder: false,
          homeButton: false,
          sceneModePicker: false,
          navigationHelpButton: false,
          animation: false,
          timeline: false,
          fullscreenButton: false,
          infoBox: false,
          selectionIndicator: false,
          // `baseLayer` (not the removed `imageryProvider` option) supplies open
          // satellite tiles so no Cesium ion account is required.
          baseLayer: new Cesium.ImageryLayer(new Cesium.UrlTemplateImageryProvider({ url: ESRI_IMAGERY })),
        });
        viewerRef.current = viewer;

        // Astronomically-correct lighting + atmosphere + stars come for free.
        viewer.scene.globe.enableLighting = true;
        viewer.scene.skyAtmosphere.show = true;
        viewer.scene.fog.enabled = true;
        viewer.scene.globe.showGroundAtmosphere = true;
        viewer.clock.shouldAnimate = false;

        // Optional real terrain (only if enabled and a free ion token is set).
        if (token && terrain) {
          try {
            viewer.terrainProvider = await Cesium.createWorldTerrainAsync();
          } catch {
            /* fall back to the smooth ellipsoid */
          }
        }

        // Draw the route as a line on the globe.
        const positions = geometry.path.map(([lat, lon]) =>
          Cesium.Cartesian3.fromDegrees(lon, lat, CRUISE_ALT_M)
        );
        viewer.entities.add({
          polyline: {
            positions,
            width: 3,
            material: new Cesium.PolylineGlowMaterialProperty({
              color: Cesium.Color.CYAN.withAlpha(0.85),
              glowPower: 0.25,
            }),
            clampToGround: false,
          },
        });

        cloudTextureRef.current = makeCloudTexture();

        if (cancelled) {
          viewer.destroy();
          return;
        }
        setPhase("ready");
        setFraction(0);
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || "Could not start the 3D view.");
          setPhase("error");
        }
      }
    })();

    return () => {
      cancelled = true;
      cancelAnimationFrame(flyRaf.current);
      const v = viewerRef.current;
      viewerRef.current = null;
      if (v && !v.isDestroyed()) v.destroy();
    };
  }, [open, canPreview, geometry]);

  // Time at a given fraction of the trip (drives sun/moon/stars position).
  const timeAt = useCallback(
    (f) => {
      const start = effDeparture instanceof Date ? effDeparture.getTime() : Date.now();
      return new Date(start + f * (geometry?.durationMs || 0));
    },
    [effDeparture, geometry]
  );

  // ── Position the cockpit camera + update the clock for a given fraction ──
  const updateCamera = useCallback(
    (f) => {
      const Cesium = cesiumRef.current;
      const viewer = viewerRef.current;
      if (!Cesium || !viewer || !geometry) return;

      const path = geometry.path;
      const maxIdx = path.length - 1;
      const pos = Math.min(Math.max(f, 0), 1) * maxIdx;
      const i = Math.min(Math.floor(pos), maxIdx - 1);
      const [lat, lon] = path[i];
      const [nlat, nlon] = path[Math.min(i + 1, maxIdx)];
      const hdg = bearing(lat, lon, nlat, nlon);

      viewer.camera.setView({
        destination: Cesium.Cartesian3.fromDegrees(lon, lat, CRUISE_ALT_M),
        orientation: {
          heading: Cesium.Math.toRadians(hdg),
          pitch: Cesium.Math.toRadians(-6), // gentle look-down, out the window
          roll: 0,
        },
      });

      viewer.clock.currentTime = Cesium.JulianDate.fromDate(timeAt(f));
    },
    [geometry, timeAt]
  );

  // Re-render clouds for the waypoint nearest the current fraction, and fetch its
  // forecast for the readout strip.
  const refreshWeather = useCallback(
    async (f) => {
      const Cesium = cesiumRef.current;
      const viewer = viewerRef.current;
      if (!Cesium || !viewer || !geometry) return;
      const path = geometry.path;
      const [lat, lon] = path[Math.round(f * (path.length - 1))];

      try {
        const t = timeAt(f).toISOString();
        const res = await fetch(`/api/forecast?lat=${lat.toFixed(3)}&lon=${lon.toFixed(3)}&time=${encodeURIComponent(t)}`);
        const data = await res.json();
        setWeather(data.weather || null);
        renderClouds(Cesium, viewer, lat, lon, data.weather);
      } catch {
        setWeather(null);
      }
    },
    [geometry, timeAt]
  );

  // Place translucent cloud billboards around a point, density from cover %.
  // Deliberately "real-ish", not volumetric — cheap and smooth on a laptop GPU.
  function renderClouds(Cesium, viewer, lat, lon, wx) {
    let coll = viewer.__clouds;
    if (!coll) {
      coll = viewer.scene.primitives.add(new Cesium.BillboardCollection());
      viewer.__clouds = coll;
    }
    coll.removeAll();
    if (!wx || !Array.isArray(wx.clouds)) return;
    const image = cloudTextureRef.current;

    for (const layer of wx.clouds) {
      const cover = Math.max(0, Math.min(100, layer.coverPct || 0));
      if (cover < 5) continue;
      const puffs = Math.round((cover / 100) * 40); // up to ~40 puffs per layer
      for (let k = 0; k < puffs; k++) {
        // Deterministic scatter within ~0.6° around the point.
        const a = (k / puffs) * Math.PI * 2 + layer.baseM;
        const r = 0.05 + ((k * 7919) % 100) / 100 * 0.55;
        const dLat = Math.cos(a) * r;
        const dLon = (Math.sin(a) * r) / Math.cos((lat * Math.PI) / 180);
        coll.add({
          position: Cesium.Cartesian3.fromDegrees(lon + dLon, lat + dLat, layer.baseM),
          image,
          sizeInMeters: true, // width/height are metres, not screen pixels
          width: 9000,
          height: 6000,
          color: Cesium.Color.WHITE.withAlpha(0.35 + (cover / 100) * 0.4),
        });
      }
    }
  }

  // Apply camera whenever the scrubber moves (and the scene is ready).
  useEffect(() => {
    if (phase !== "ready") return;
    updateCamera(fraction);
  }, [fraction, phase, updateCamera]);

  // Debounced weather refresh as the fraction changes.
  useEffect(() => {
    if (phase !== "ready") return;
    const id = setTimeout(() => refreshWeather(fraction), 250);
    return () => clearTimeout(id);
  }, [fraction, phase, refreshWeather]);

  // Fly animation — advance the fraction over ~30s of wall-clock.
  const toggleFly = useCallback(() => {
    if (flying) {
      cancelAnimationFrame(flyRaf.current);
      setFlying(false);
      return;
    }
    setFlying(true);
    const startF = fraction >= 1 ? 0 : fraction;
    const startT = performance.now();
    const DURATION = 30000;
    const step = (now) => {
      const f = Math.min(1, startF + (now - startT) / DURATION);
      setFraction(f);
      if (f < 1) {
        flyRaf.current = requestAnimationFrame(step);
      } else {
        setFlying(false);
      }
    };
    flyRaf.current = requestAnimationFrame(step);
  }, [flying, fraction]);

  // Esc closes.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const now = timeAt(fraction);

  return (
    <div className="preview3d" role="dialog" aria-modal="true" aria-label="3D flight preview">
      <div className="preview3d-canvas" ref={containerRef} />

      <header className="preview3d-head">
        <div className="preview3d-title">
          🛩️ {canPreview ? `${fmtAirport(effRoute?.origin)} → ${fmtAirport(effRoute?.destination)}` : "Plan a flight"}
          {geometry ? <span className="muted"> · {Math.round(geometry.totalKm).toLocaleString()} km</span> : null}
        </div>
        <button className="preview3d-close" onClick={onClose} aria-label="Close 3D preview">
          ✕
        </button>
      </header>

      {phase === "loading" && (
        <div className="preview3d-status" role="status">
          Loading 3D…
        </div>
      )}
      {phase === "error" && (
        <div className="preview3d-status preview3d-error" role="alert">
          {error || "Could not start the 3D view."}
        </div>
      )}
      {!canPreview && (
        <ManualRouteForm
          onLucky={onLucky}
          onSubmit={(r, dep) => {
            setManualRoute(r);
            setDepTime(dep);
          }}
        />
      )}

      {phase === "ready" && (
        <>
          <div className="preview3d-readout" aria-live="polite">
            <span>🕑 {now.toISOString().slice(0, 16).replace("T", " ")}Z</span>
            {weather && (
              <>
                {weather.cloudCoverPct != null && <span>☁️ {Math.round(weather.cloudCoverPct)}%</span>}
                {weather.windKt != null && <span>💨 {Math.round(weather.windKt)} kt</span>}
                {weather.tempC != null && <span>🌡️ {Math.round(weather.tempC)}°C</span>}
                {weather.visibilityM != null && <span>👁️ {Math.round(weather.visibilityM / 1000)} km</span>}
              </>
            )}
          </div>

          <div className="preview3d-controls">
            <button className="preview3d-btn" onClick={toggleFly}>
              {flying ? "⏸ Pause" : "▶ Fly route"}
            </button>
            <input
              type="range"
              className="preview3d-scrub"
              min={0}
              max={1}
              step={0.001}
              value={fraction}
              onChange={(e) => {
                setFlying(false);
                cancelAnimationFrame(flyRaf.current);
                setFraction(Number(e.target.value));
              }}
              aria-label="Scrub through the flight"
            />
            <span className="preview3d-pct">{Math.round(fraction * 100)}%</span>
          </div>
        </>
      )}
    </div>
  );
}

// Small form shown when the overlay is opened without a flight — the user types
// two airport codes (and an optional departure time); we resolve them to
// coordinates via /api/airport and hand back a route object.
function ManualRouteForm({ onSubmit, onLucky }) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [when, setWhen] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Pick a surprise future flight departing from the current map view.
  function lucky() {
    setErr("");
    try {
      const pick = onLucky?.();
      if (pick?.origin && pick?.destination) {
        onSubmit({ origin: pick.origin, destination: pick.destination }, pick.departure || new Date());
      }
    } catch {
      setErr("Could not pick a lucky flight — try entering a route.");
    }
  }

  async function resolve(code) {
    const res = await fetch(`/api/airport/${encodeURIComponent(code.trim().toUpperCase())}`);
    const data = await res.json();
    if (!data.airport) throw new Error(`Airport “${code.toUpperCase()}” not found`);
    return data.airport;
  }

  async function submit(e) {
    e.preventDefault();
    setErr("");
    if (!from.trim() || !to.trim()) {
      setErr("Enter both a departure and destination airport.");
      return;
    }
    setBusy(true);
    try {
      const [origin, destination] = await Promise.all([resolve(from), resolve(to)]);
      const dep = when ? new Date(when) : new Date();
      onSubmit({ origin, destination }, dep);
    } catch (e2) {
      setErr(e2.message || "Could not resolve those airports.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="preview3d-form" onSubmit={submit}>
      <h2>Preview a flight in advance</h2>
      <p className="muted">Enter two airport codes (ICAO, e.g. EGLL / KJFK) to fly the route.</p>
      <div className="preview3d-form-row">
        <input
          aria-label="Departure airport (ICAO)"
          placeholder="From (EGLL)"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          autoFocus
        />
        <span aria-hidden="true">→</span>
        <input
          aria-label="Destination airport (ICAO)"
          placeholder="To (KJFK)"
          value={to}
          onChange={(e) => setTo(e.target.value)}
        />
      </div>
      <label className="preview3d-form-when">
        Departure time (optional)
        <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} />
      </label>
      {err && (
        <p className="preview3d-form-err" role="alert">
          {err}
        </p>
      )}
      <div className="preview3d-form-actions">
        <button className="preview3d-btn" type="submit" disabled={busy}>
          {busy ? "Finding airports…" : "Preview flight"}
        </button>
        {onLucky && (
          <button
            className="preview3d-btn preview3d-lucky"
            type="button"
            onClick={lucky}
            disabled={busy}
            title="Fly a surprise flight, departing 1–3 days from now out of the current map view"
          >
            🍀 I'm feeling lucky
          </button>
        )}
      </div>
    </form>
  );
}
